import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveDynamicComponent as _resolveDynamicComponent, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  key: 1,
  class: "overflow-hidden"
}

import { computed } from 'vue';
import ResponsiveImage from '@/core/responsive/image/ResponsiveImage.vue';
import VimeoEmbed from '@/shared/media/VimeoEmbed.vue';
import breakpointsState from '@/core/responsive/breakpoints/breakpoints-state.observable';
import constants from '@/shared/config/constants';
import utils from '@/shared/utilities.service';
import { MediaModel } from '@/types/B2BcontentServerContract';
import LazyInit from '@/core/rendering/LazyInit.vue';
import siteContextStore from '@/shared/content/site-context.store';
import FallbackImageTransition from '@/shared/media/transitions/FallbackImageTransition.vue';
import ImageTransition from '@/shared/media/transitions/ImageTransition.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'MediaHandler',
  props: {
    imageWidthOnScreen: { default: undefined },
    imageAltTextOverride: { default: undefined },
    imageZoomOverride: { type: [Boolean, null], default: undefined },
    mediaAspectRatio: { default: undefined },
    mediaBlock: null,
    videoDisableOnMobile: { type: Boolean, default: false },
    autoFitVideoToAspectRatio: { type: Boolean, default: true },
    videoAutoplayOverride: { type: [Boolean, null], default: undefined }
  },
  setup(__props: any) {

const props = __props as { imageWidthOnScreen: number | Object, imageAltTextOverride: string | undefined, imageZoomOverride: boolean | undefined, mediaAspectRatio: number | Object, mediaBlock: MediaModel, videoDisableOnMobile: boolean, autoFitVideoToAspectRatio: boolean, videoAutoplayOverride: boolean | undefined }




const isImageType = computed(() => {
    return (props.mediaBlock && props.mediaBlock.type === constants.media.imageModel) ?? false;
});

const isVimeoWithFallbackImage = computed(() => {
    return (props.mediaBlock && props.mediaBlock.type === constants.media.vimeoModel && !!props.mediaBlock.model.image) ?? false;
});

const disableVideoOnMobile = computed(() => {
    const isMobileOrTablet = breakpointsState.activeBreakpoint === 'xs' || breakpointsState.activeBreakpoint === 'sm' || breakpointsState.activeBreakpoint === 'md';
    return isMobileOrTablet && (props.videoDisableOnMobile || siteContextStore.heroBannerSettings.disableVideoOnMobile);
});

const showImage = computed(() => {
    return ((isImageType.value || isVimeoWithFallbackImage.value) || disableVideoOnMobile.value) && !!props.mediaBlock.model;
});

const imageTransition = computed(() => {
    return showImage.value && isVimeoWithFallbackImage.value ? FallbackImageTransition : ImageTransition;
});

const autoPlayVideo = computed(() => {
    if (props.videoAutoplayOverride !== undefined) {
        return props.videoAutoplayOverride;
    } else if (props.mediaBlock.overrideProperties && props.mediaBlock.overrideProperties.autoPlay !== false) {
        return true;
    }

    return props.mediaBlock.model.autoPlay;
});

const vimeoId = computed(() => {
    const idFromVimeoUrl = utils.ifMediaModelIsVideoReturnIdFromVimeoUrl(props.mediaBlock);
    return idFromVimeoUrl ? idFromVimeoUrl.toString() : '';
});

const showVideo = computed(() => {
    const isMobile = breakpointsState.activeBreakpoint === 'xs' || breakpointsState.activeBreakpoint === 'sm';
    const videoIsDisabled = isMobile && props.videoDisableOnMobile;
    return !videoIsDisabled && vimeoId.value !== '' && !disableVideoOnMobile.value && !siteContextStore.heroBannerSettings.disableVideoOnAllDevices;
});

const imageUrl = computed(() => {
    if (props.mediaBlock.model.image) {
        return props.mediaBlock.model.image.url;
    }
    return props.mediaBlock.model.url;
});

const focus = computed<boolean>(() => {
    if (props.mediaBlock.overrideProperties?.focus || props.mediaBlock.model.image?.focus) {
        return true;
    }
    return props.mediaBlock.model.focus;
});

const focusPoints = computed<string>(() => {
    if (props.mediaBlock.model.image) {
        return props.mediaBlock.model.image.focusPoints;
    }
    return props.mediaBlock.model.focusPoints;
});

const zoom = computed<boolean>(() => {
    if (props.mediaBlock.overrideProperties?.zoom) {
        return true;
    }
    return props.mediaBlock.model.zoom;
});

const imageAltText = computed(() => {
    if (props.imageAltTextOverride !== undefined) {
        return props.imageAltTextOverride;
    }
    return props.mediaBlock.model.description;
});

const originalWidth = computed(() => {
    if (props.mediaBlock.model.image?.width) {
        return props.mediaBlock.model.image.width;
    }
    return props.mediaBlock.model.width;
});

const originalHeight = computed(() => {
    if (props.mediaBlock.model.image?.height) {
        return props.mediaBlock.model.image.height;
    }
    return props.mediaBlock.model.height;
});

return (_ctx: any,_cache: any) => {
  return (_unref(showVideo))
    ? (_openBlock(), _createBlock(LazyInit, { key: 0 }, {
        default: _withCtx(() => [
          _createVNode(VimeoEmbed, {
            "src-id": _unref(vimeoId),
            autoplay: _unref(autoPlayVideo),
            "auto-fit-video-to-aspect-ratio": __props.autoFitVideoToAspectRatio,
            "aspect-ratio": __props.mediaAspectRatio,
            title: __props.mediaBlock.model.description,
            class: _normalizeClass({'pointer-events-none': _unref(autoPlayVideo)})
          }, null, 8, ["src-id", "autoplay", "auto-fit-video-to-aspect-ratio", "aspect-ratio", "title", "class"])
        ]),
        _: 1
      }))
    : (_unref(showImage))
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(imageTransition)), null, {
            default: _withCtx(() => [
              _createVNode(ResponsiveImage, {
                "image-url": _unref(imageUrl),
                "aspect-ratio": __props.mediaAspectRatio,
                alt: _unref(imageAltText),
                "width-on-screen": __props.imageWidthOnScreen,
                flip: __props.mediaBlock.model.flip,
                "original-height": _unref(originalHeight),
                "original-width": _unref(originalWidth),
                "cache-bust": __props.mediaBlock.model.cachebust,
                focus: _unref(focus),
                "focus-points": _unref(focusPoints),
                zoom: _unref(zoom)
              }, null, 8, ["image-url", "aspect-ratio", "alt", "width-on-screen", "flip", "original-height", "original-width", "cache-bust", "focus", "focus-points", "zoom"])
            ]),
            _: 1
          }))
        ]))
      : _createCommentVNode("", true)
}
}

})